import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField
} from '@mui/material';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { enableFetchObjectsAtom } from '@/pages/Administration/Devices/Devices.store';

import useDeviceVulnerabilityStyles from '../DeviceVulnerability/DeviceVulnerability.styles';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import type { DynamicFieldData } from '../SourceCreate.types';

export type FetchCustomObjectsProps = {
  fetchObjSchema: DynamicFieldData[];
};
const FetchCustomObjects = ({ fetchObjSchema }: FetchCustomObjectsProps) => {
  const { classes } = useDeviceVulnerabilityStyles();

  // Form State
  const { control, formState, register, unregister, getValues } =
    useFormContext();
  const { errors } = formState;

  const [enableFetchObjects, setEnableFetchObjects] = useAtom(
    enableFetchObjectsAtom
  );

  const handleFetchDeviceVulnerabilitiesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEnableFetchObjects(event.target.checked);
  };
  const validateCustomObjects = () => {
    // Sales force Custom Object Validation
    let msge: boolean | string = true;
    if (enableFetchObjects) {
      if (getValues('custom_objects') === '') {
        msge = 'Custom Objects is required';
      } else {
        const customObjects = getValues('custom_objects').split(',');
        if (customObjects.length > 0) {
          customObjects.forEach((obj: string) => {
            if (
              obj.trim() !== '' &&
              obj.length > 3 &&
              obj.endsWith('__c') === false
            ) {
              msge = `Invalid Custom object(s) -${obj}`;
            }
          });
        }
      }
    }
    return msge;
  };

  useEffect(() => {
    if (enableFetchObjects) {
      register('custom_objects', { validate: () => validateCustomObjects() });
    } else {
      unregister(['custom_objects']);
    }
  }, [enableFetchObjects]);
  return (
    <>
      <Grid size={{ xs: 12, sm: 12, md: 12 }}>
        <h2>
          {' '}
          <Controller
            control={control}
            name={fetchObjSchema[0]?.name}
            defaultValue={false}
            render={({ field }) => (
              <InputLabel
                className={classes.fetchTimeLabel}
                htmlFor="device-name-input"
              >
                <span>{fetchObjSchema[0]?.label}</span>
                <Switch
                  className={classes.fetchTimeSwitch}
                  defaultValue="false"
                  {...field}
                  checked={enableFetchObjects}
                  onChange={(value) => {
                    field.onChange(value);
                    handleFetchDeviceVulnerabilitiesChange(value);
                  }}
                  value={field.value}
                />
              </InputLabel>
            )}
          />
          <hr />
        </h2>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel
          className={classes.fetchTimeLabel}
          htmlFor={fetchObjSchema[1]?.name}
        >
          Fetch Polling Interval
        </InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={fetchObjSchema[1]?.name}
            defaultValue="10 Minutes"
            rules={{ required: 'Polling Interval is required' }}
            render={({ field }) => (
              <Select
                disabled={!enableFetchObjects}
                {...field}
                onChange={field.onChange}
                value={field.value}
                sx={{ width: '100%' }}
              >
                {fetchObjSchema[1]?.options?.map((item: string) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <InputLabel htmlFor="source_type_input">
          Ingest From (Days) *
        </InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={fetchObjSchema[2]?.name}
            defaultValue="1"
            rules={{ required: 'Ingest From is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value}
                error={!!errors?.custom_object_ingest_from}
                disabled={!enableFetchObjects}
                type="number"
                slotProps={{ htmlInput: { min: 1, max: 7 } }}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
        <InputLabel htmlFor="source_type_input">
          Custom Objects * (use comma for multiple objects)
        </InputLabel>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={fetchObjSchema[3]?.name}
            defaultValue=""
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value}
                error={!!errors?.custom_objects}
                disabled={!enableFetchObjects}
                type="text"
                multiline
              />
            )}
          />
          {errors?.custom_objects && (
            <ErrorMessage message={errors?.custom_objects?.message} />
          )}
        </FormControl>
      </Grid>
    </>
  );
};

export default FetchCustomObjects;
